/**
 * Screening libraries and screen types — the values the library-screen job accepts.
 *
 * A screening "library" is a panel of protein TARGETS, not compounds. Each
 * submitted compound is scored against every item in it: a 536-row result set
 * per compound, keyed by `library_item_id` (p38a, HCK, FGFR1, CDK2, SRC, …).
 * So "screen my library" is a kinase selectivity panel, and the protein loaded
 * in the workspace has no bearing on it.
 *
 * These lived as module-private constants in `ScreeningPane/Tabs/NewScreen.jsx`,
 * which made the panel the only thing that could know what a valid library is.
 * Gibbs needs the same list to offer the choice in chat, and two copies of a
 * list whose keys are sent to a job API is how they drift. They live here, at
 * the job layer, because they describe what the job accepts rather than how any
 * one UI presents it.
 *
 * @module jobman/screeningLibraries
 */

import { Loader } from '../Loader';
import { conditionalArrayItem } from '../util/js_utils';

/**
 * Target panels the job will screen against. `key` is what rides the job
 * request as `library.id`.
 *
 * A function, not a module-level array, because the lab-features entry reads
 * `Loader`. This module is reachable from the Gibbs knowledge tree, which Next pulls
 * into the server render, and touching `Loader` at import time fails the build
 * with "Failed to collect page data" — it does not exist yet at that point.
 * In `NewScreen.jsx` the same expression was safe only because nothing imported
 * it server-side.
 *
 * @returns {{key: string, label: string}[]}
 */
const LIBRARIES = [
    { key: 'all-human-kinases', label: 'Human Kinases - All (536)' },
    { key: 'human-kinase-subset', label: 'Human Kinases - Diverse Subset (50)' },
    { key: '3-kinases', label: 'Human Kinases - Test 3 (BRAF, p38, JAK3)', labOnly: true },
];

/**
 * Every library key, lab-gated ones included, and WITHOUT reading `Loader`.
 *
 * The registry declares `open-screening-panel`'s `library` options, and an ActionDef is a
 * module-scope object -- calling `getLibraryOptions()` there would touch `Loader` at import time
 * and fail the Next build with "Failed to collect page data", which is the hazard this module's
 * header already describes. Availability still gates in `getLibraryOptions`; only the names of
 * the keys are published here, so the validator has one source to check against instead of a
 * third copy of the list.
 */
export const ALL_LIBRARY_KEYS = LIBRARIES.map(({ key }) => key);

export function getLibraryOptions() {
    return LIBRARIES
        .filter(({ labOnly }) => !labOnly || Loader.AllowLabFeatures)
        .map(({ key, label }) => ({ key, label }));
}

/**
 * Scoring configurations. One entry today; the shape is kept as a list because
 * the job request carries `{jobtype, subtype}` rather than a single name.
 */
export const screenTypeOptions = [
    {
        key: 'boltz2_folding', label: 'Boltz-2 Folding', jobtype: 'folding', subtype: 'Boltz-2',
    },
];

/**
 * @param {string} key
 * @returns {Object|null} the library option, or null when the key is unknown
 */
export function findLibrary(key) {
    return getLibraryOptions().find((item) => item.key === key) || null;
}

/**
 * @param {string} key
 * @returns {Object|null} the screen-type option, or null when the key is unknown
 */
export function findScreenType(key) {
    return screenTypeOptions.find((item) => item.key === key) || null;
}
